import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { organizations as organizationsApi, projects as projectsApi } from "../api/resources";
import type { OnboardingProject, OrganizationSummary } from "./types";

const STORAGE_KEY = "discovery.currentOrganizationId";

interface OrganizationContextValue {
  organizations: OrganizationSummary[];
  current: OrganizationSummary | undefined;
  currentProject: OnboardingProject | undefined;
  loading: boolean;
  error: string | undefined;
  select: (organizationId: string) => void;
  refresh: () => void;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(() =>
    typeof localStorage === "undefined" ? null : localStorage.getItem(STORAGE_KEY),
  );
  const [currentProject, setCurrentProject] = useState<OnboardingProject | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    organizationsApi
      .list({ limit: 200 })
      .then((page) => {
        if (cancelled) return;
        setOrganizations(page.items);
        setError(undefined);
        setCurrentId((existing) => {
          if (existing && page.items.some((row) => row.id === existing)) return existing;
          return page.items[0]?.id ?? null;
        });
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  useEffect(() => {
    if (currentId) localStorage.setItem(STORAGE_KEY, currentId);
  }, [currentId]);

  useEffect(() => {
    if (!currentId) {
      setCurrentProject(undefined);
      return;
    }
    let cancelled = false;
    projectsApi
      .list(currentId)
      .then((rows) => {
        if (cancelled) return;
        const active =
          rows.find((row) => ["draft", "in_progress", "in_review"].includes(row.status)) ?? rows[0];
        setCurrentProject(active);
      })
      .catch(() => {
        if (!cancelled) setCurrentProject(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [currentId, nonce]);

  const value = useMemo<OrganizationContextValue>(
    () => ({
      organizations,
      current: organizations.find((row) => row.id === currentId),
      currentProject,
      loading,
      error,
      select: (organizationId: string) => setCurrentId(organizationId),
      refresh: () => setNonce((n) => n + 1),
    }),
    [organizations, currentId, currentProject, loading, error],
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganizations(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (!context) throw new Error("useOrganizations must be used inside OrganizationProvider");
  return context;
}

/** Convenience for org-scoped pages: throws only if rendered outside the shell. */
export function useCurrentOrganization() {
  const { current, currentProject, refresh } = useOrganizations();
  const organizationId = current?.id;
  const reload = useCallback(() => refresh(), [refresh]);
  return { organization: current, organizationId, project: currentProject, reload };
}
